import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, FormControlLabel, Checkbox, TextField,
  Box, Typography, CircularProgress, Alert,
} from '@mui/material';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { logAudit, AUDIT_ACTIONS } from '../utils/auditLog';
import i18n from 'i18next';

const EULA_VERSION = '1.0';

export default function EulaModal({ user, userData }) {
  const [open, setOpen]       = useState(false);
  const [checked, setChecked] = useState(false);
  const [name, setName]       = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'eula_acceptances', user.uid));
        if (!snap.exists() || (snap.data().acceptedVersion ?? '0') < EULA_VERSION) {
          setName(user.displayName || '');
          setOpen(true);
        }
      } catch (_) {
        // se non riusciamo a leggere, mostriamo il modal per sicurezza
        setName(user.displayName || '');
        setOpen(true);
      }
    })();
  }, [user]);

  const handleAccept = async () => {
    if (!checked || !name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const eulaPayload = {
        userId:          user.uid,
        email:           user.email || '',
        name:            name.trim(),
        teamId:          userData?.teamId || null,
        acceptedAt:      serverTimestamp(),
        acceptedVersion: EULA_VERSION,
        ua:              navigator.userAgent.substring(0, 300),
      };
      await setDoc(doc(db, 'eula_acceptances', user.uid), eulaPayload);
      // eulaLog è leggibile dal super admin (cade sotto la regola catch-all)
      await setDoc(doc(db, 'eulaLog', user.uid), eulaPayload);
      await logAudit(user.uid, userData?.teamId, AUDIT_ACTIONS.EULA_ACCEPT, {
        version: EULA_VERSION,
        email:   user.email,
        name:    name.trim(),
      });
      setOpen(false);
    } catch (e) {
      setError('Errore durante il salvataggio. Riprova.');
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      onClose={(_, reason) => { if (reason !== 'backdropClick') setOpen(false); }}
    >
      <DialogTitle sx={{ borderBottom: '2px solid', borderColor: 'error.main', pb: 1.5 }}>
        <Typography variant="h6" fontWeight={700}>
          {i18n.language?.startsWith('en') ? 'Terms of Service — MotorSportSuite' : 'Termini di Servizio — MotorSportSuite'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {i18n.language?.startsWith('en') ? 'You must accept before continuing' : 'È necessario accettare prima di continuare'}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ height: 440, borderBottom: '1px solid', borderColor: 'divider' }}>
          <iframe
            src={i18n.language?.startsWith('en') ? '/docs/eula-en.html' : '/docs/eula-it.html'}
            title="EULA MotorSportSuite"
            style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
          />
        </Box>

        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <TextField
            label={i18n.language?.startsWith('en') ? 'Your full name *' : 'Il tuo nome e cognome *'}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            placeholder={i18n.language?.startsWith('en') ? 'John Smith' : 'Mario Rossi'}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => setChecked(e.target.checked)}
                color="error"
              />
            }
            label={
              i18n.language?.startsWith('en') ? (
                <Typography variant="body2">
                  I have read and fully accept the Terms of Service, the EULA and the{' '}
                  <a href="/docs/privacy-en.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  {' '}of MotorSportSuite. I confirm I am authorised to accept on behalf of the organisation I represent.
                </Typography>
              ) : (
                <Typography variant="body2">
                  Ho letto integralmente e accetto i Termini di Servizio, l'EULA e l'
                  <a href="/docs/privacy-it.html" target="_blank" rel="noopener noreferrer">Informativa Privacy</a>
                  {' '}di MotorSportSuite. Confermo di essere autorizzato ad accettare per conto dell'organizzazione che rappresento.
                </Typography>
              )
            }
          />
          {error && <Alert severity="error" sx={{ mt: 0 }}>{error}</Alert>}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {i18n.language?.startsWith('en')
            ? 'Acceptance is recorded with date, time and device.'
            : 'L\'accettazione viene registrata con data, ora e dispositivo.'}
        </Typography>
        <Button
          variant="contained"
          color="error"
          onClick={handleAccept}
          disabled={!checked || !name.trim() || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving
            ? (i18n.language?.startsWith('en') ? 'Saving…' : 'Salvataggio…')
            : (i18n.language?.startsWith('en') ? 'Accept and Continue' : 'Accetta e Continua')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
